import { Button, Flex, Progress, Typography } from "antd";
import dayjs from "dayjs";
import { formatNumber } from "libs/format_utils";
import { pluralize } from "libs/utils";
import type { APIOrganization } from "types/api_types";
import {
  useActiveOrganization,
  useCanRequestUpgrades,
  useShouldContactSalesForUpgrades,
} from "./organization_hooks";
import {
  canExtendPricingPlan,
  contactSales,
  formatPricingPlanName,
  getAiAddonIncludedCredits,
  getBasePricingPlan,
  getDaysUntilPlanExpires,
  isAiAddonEligiblePlan,
  isTrialPlan,
  PLAN_EXPIRATION_REMINDER_DAYS,
  PricingPlanEnum,
} from "./pricing_plan_utils";
import UpgradePricingPlanModal from "./upgrade_plan_modal";

type SidebarPlanHint =
  | { kind: "planExpiring"; daysLeft: number }
  | { kind: "teamUpgrade"; isOpenSource: boolean }
  | { kind: "contactSales" }
  | { kind: "aiAddon"; includedCredits: number };

function getSidebarPlanHint(
  organization: APIOrganization,
  shouldContactSales: boolean,
): SidebarPlanHint | null {
  const daysLeft = getDaysUntilPlanExpires(organization);
  // Expired plans (0 days left) are included, so they get the extension hint rather than another offer.
  if (
    daysLeft != null &&
    canExtendPricingPlan(organization) &&
    (isTrialPlan(organization.pricingPlan) || daysLeft <= PLAN_EXPIRATION_REMINDER_DAYS)
  ) {
    return { kind: "planExpiring", daysLeft };
  }
  if (shouldContactSales) {
    return { kind: "contactSales" };
  }
  if (
    organization.pricingPlan === PricingPlanEnum.Personal ||
    organization.pricingPlan === PricingPlanEnum.OpenSource
  ) {
    return {
      kind: "teamUpgrade",
      isOpenSource: organization.pricingPlan === PricingPlanEnum.OpenSource,
    };
  }
  if (organization.aiPlan == null && isAiAddonEligiblePlan(organization.pricingPlan)) {
    return {
      kind: "aiAddon",
      includedCredits: getAiAddonIncludedCredits(organization.pricingPlan),
    };
  }
  return null;
}

function useSidebarPlanHint(): SidebarPlanHint | null {
  const organization = useActiveOrganization();
  const canRequestUpgrades = useCanRequestUpgrades();
  const shouldContactSales = useShouldContactSalesForUpgrades();
  return canRequestUpgrades ? getSidebarPlanHint(organization, shouldContactSales) : null;
}

function PlanHintCard({
  title,
  description,
  progressPercent,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  progressPercent?: number;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Flex
      vertical
      gap={8}
      style={{
        padding: 16,
        borderRadius: 6,
        border: "1px solid var(--ant-color-primary-border)",
        background: "var(--ant-color-primary-bg)",
      }}
    >
      <Typography.Text strong>{title}</Typography.Text>
      {progressPercent != null ? (
        <Progress
          percent={progressPercent}
          showInfo={false}
          size={{ height: 6 }}
          railColor="var(--ant-color-primary-border)"
          style={{ margin: 0, lineHeight: 0 }}
        />
      ) : null}
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {description}
      </Typography.Text>
      <Button type="primary" size="small" onClick={onAction} style={{ alignSelf: "flex-start" }}>
        {actionLabel}
      </Button>
    </Flex>
  );
}

function PlanExpiringHintCard({ daysLeft }: { daysLeft: number }) {
  const organization = useActiveOrganization();
  const planLabel = isTrialPlan(organization.pricingPlan) ? "Trial" : "Plan";
  const basePlan = getBasePricingPlan(organization.pricingPlan);
  const featuresLabel = formatPricingPlanName(basePlan);
  const extendPlan = () => UpgradePricingPlanModal.extendPricingPlan();

  if (daysLeft === 0) {
    return (
      <PlanHintCard
        title={`${planLabel} expired`}
        description={`Extend it to get ${featuresLabel} features back.`}
        actionLabel="Extend Plan"
        onAction={extendPlan}
      />
    );
  }

  // The progress bar fills up over the reminder window, not the whole (unknown) plan duration.
  const elapsedDays = Math.max(0, PLAN_EXPIRATION_REMINDER_DAYS - daysLeft);

  return (
    <PlanHintCard
      title={`${planLabel} ends in ${daysLeft} ${pluralize("day", daysLeft)}`}
      progressPercent={(elapsedDays / PLAN_EXPIRATION_REMINDER_DAYS) * 100}
      description={`Keep ${featuresLabel} features after ${dayjs(organization.paidUntil).format("D MMM")}.`}
      actionLabel="Extend Plan"
      onAction={extendPlan}
    />
  );
}

// Open Source already includes the collaboration features of Team, but no jobs and AI features.
function TeamUpgradeHintCard({ isOpenSource }: { isOpenSource: boolean }) {
  return (
    <PlanHintCard
      title={isOpenSource ? "Need AI or processing jobs?" : "Working with others?"}
      description={
        isOpenSource
          ? "Team adds AI analysis, processing jobs and the AI Add-on."
          : "Team adds collaborative annotation and project management."
      }
      actionLabel="Upgrade to Team"
      onAction={() => UpgradePricingPlanModal.upgradePricingPlan(PricingPlanEnum.Team)}
    />
  );
}

function ContactSalesHintCard() {
  return (
    <PlanHintCard
      title="Need AI or processing jobs?"
      description="Talk to us about a hosted plan on webknossos.org or an Enterprise plan for your own servers."
      actionLabel="Contact Sales"
      onAction={contactSales}
    />
  );
}

function AiAddonHintCard({ includedCredits }: { includedCredits: number }) {
  return (
    <PlanHintCard
      title="Train your own AI models"
      description={`The AI Add-on includes GPU compute and ${formatNumber(includedCredits)} credits.`}
      actionLabel="Get AI Add-on"
      onAction={() => UpgradePricingPlanModal.requestAiPlanUpgrade()}
    />
  );
}

export function OrganizationSidebarPlanHint() {
  const planHint = useSidebarPlanHint();
  switch (planHint?.kind) {
    case "planExpiring":
      return <PlanExpiringHintCard daysLeft={planHint.daysLeft} />;
    case "teamUpgrade":
      return <TeamUpgradeHintCard isOpenSource={planHint.isOpenSource} />;
    case "contactSales":
      return <ContactSalesHintCard />;
    case "aiAddon":
      return <AiAddonHintCard includedCredits={planHint.includedCredits} />;
    default:
      return null;
  }
}
